/**
 * Manage tab — Supervisor+ view across the team / branch.
 *
 * Until a server-side aggregator route ships (`/v3/appointments/branch/:code`),
 * this tab unions calendar + Bookings appointments visible to the signed-in
 * user and exposes lightweight reassign / cancel affordances.
 */

import { useMemo, useState } from 'react';
import { Pill, Button, Banner, DataGrid, type DataGridColumn } from '@/components/primitives';
import { useTodayCalendar } from '../hooks/useTodayCalendar';
import { useUpcomingCalendar } from '../hooks/useUpcomingCalendar';
import { useBranchBookings } from '../hooks/useBranchBookings';
import type { Appointment, AppointmentChannel } from '../types';

const CHANNEL_LABEL: Record<AppointmentChannel, string> = {
  inBranch: 'In-branch',
  phone: 'Phone',
  teams: 'Teams',
  bookings: 'Bookings',
};

interface Row {
  id: string;
  customer: string;
  subject: string;
  whenLabel: string;
  channel: string;
  officer: string;
  source: string;
  branch: string;
  appt: Appointment;
}

export function ManageTab(): JSX.Element {
  const today = useTodayCalendar();
  const upcoming = useUpcomingCalendar();
  const bookings = useBranchBookings();
  const [filter, setFilter] = useState<'all' | 'today' | 'unassigned'>('all');
  // BACKLOG-1297 — the reassign / cancel affordances used to answer with
  // window.alert(), which the Teams webview swallows: the button looked dead.
  // The same message now lands in a banner above the grid.
  const [notice, setNotice] = useState<string | null>(null);

  const rows = useMemo<Row[]>(() => {
    const map = new Map<string, Appointment>();
    for (const a of today.appointments) map.set(a.id, a);
    for (const a of upcoming.appointments) if (!map.has(a.id)) map.set(a.id, a);
    for (const a of bookings.appointments) if (!map.has(a.id)) map.set(a.id, a);
    let list = Array.from(map.values());
    if (filter === 'today') {
      const now = new Date().toDateString();
      list = list.filter((a) => new Date(a.startUtc).toDateString() === now);
    }
    if (filter === 'unassigned') {
      list = list.filter((a) => !a.assignedOfficer || a.assignedOfficer.includes('('));
    }
    list.sort((a, b) => a.startUtc.localeCompare(b.startUtc));
    return list.map<Row>((a) => ({
      id: a.id,
      customer: a.customerName,
      subject: a.subject,
      whenLabel: new Date(a.startUtc).toLocaleString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      }),
      channel: CHANNEL_LABEL[a.channel],
      officer: a.assignedOfficer ?? '(unassigned)',
      source: a.source === 'bookings' ? 'Bookings' : 'Calendar',
      branch: a.branchName ?? '—',
      appt: a,
    }));
  }, [today.appointments, upcoming.appointments, bookings.appointments, filter]);

  const columns: DataGridColumn<Row>[] = [
    { key: 'customer', label: 'Customer', render: (r) => r.customer },
    { key: 'subject', label: 'Subject', render: (r) => r.subject },
    { key: 'whenLabel', label: 'When', render: (r) => r.whenLabel },
    { key: 'channel', label: 'Channel', render: (r) => r.channel },
    { key: 'officer', label: 'Officer', render: (r) => r.officer },
    { key: 'source', label: 'Source', render: (r) => r.source },
    { key: 'branch', label: 'Branch', render: (r) => r.branch },
    {
      key: 'actions',
      label: 'Actions',
      render: (r) => (
        <div className="appt-manage__actions">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setNotice(`Reassigning ${r.customer}'s appointment isn't available yet — the assignment service is still being wired up.`)}
          >
            Reassign
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setNotice(`Cancelling ${r.customer}'s appointment isn't available yet — the cancellation service is still being wired up.`)}
          >
            Cancel
          </Button>
        </div>
      ),
    },
  ];

  const loading = today.loading || upcoming.loading || bookings.loading;

  return (
    <section className="appt-tab" aria-label="Manage appointments (team / branch)">
      <div className="appt-tab__bar">
        <div>
          <h2>Manage · team &amp; branch</h2>
          <span className="appt-tab__bar-sub">
            Reassign, cancel, or jump into any appointment in your span of control.
          </span>
        </div>
        <div className="appt-manage__filters">
          <Button size="sm" variant={filter === 'all' ? 'primary' : 'ghost'} onClick={() => setFilter('all')}>All</Button>
          <Button size="sm" variant={filter === 'today' ? 'primary' : 'ghost'} onClick={() => setFilter('today')}>Today</Button>
          <Button size="sm" variant={filter === 'unassigned' ? 'primary' : 'ghost'} onClick={() => setFilter('unassigned')}>Unassigned</Button>
        </div>
      </div>

      {notice && (
        <Banner tone="info">
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-4)' }}>
            <span style={{ flex: 1 }}>{notice}</span>
            <Button size="sm" variant="ghost" onClick={() => setNotice(null)}>Dismiss</Button>
          </div>
        </Banner>
      )}

      {loading && <div className="appt-tab__skeleton" role="status">Loading team appointments…</div>}

      {!loading && rows.length === 0 && (
        <div className="appt-empty">
          <Pill variant="muted">No appointments to manage right now</Pill>
          <p>The grid populates with calendar + Bookings appointments visible to you.</p>
        </div>
      )}

      {!loading && rows.length > 0 && (
        <DataGrid<Row> columns={columns} rows={rows} rowKey={(r) => r.id} />
      )}
    </section>
  );
}
